import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { resetPasswordWithToken } from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'

export function ResetPasswordPage() {
    const navigate = useNavigate()
    // Link from the reset or invitation mail: /reset-passwort?email=…&token=…
    const [searchParams] = useSearchParams()
    const email = searchParams.get('email') ?? ''
    const token = searchParams.get('token') ?? ''
    const hasLink = email.length > 0 && token.length > 0
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')

    const resetMutation = useMutation({
        mutationFn: async () => {
            if (password !== confirmPassword) {
                throw new Error('PASSWORDS_DO_NOT_MATCH')
            }
            if (password.length < 6) {
                throw new Error('PASSWORD_TOO_SHORT')
            }
            await resetPasswordWithToken({ email, token, password })
        },
        onSuccess: () => {
            toast.success('Passwort erfolgreich geändert. Du kannst dich jetzt einloggen.')
            navigate('/login', { replace: true })
        },
        onError: (error) => {
            toast.error(getErrorMessage(error))
        },
    })

    return (
        <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="items-center text-center">
                    <img src="/motionlab.png" alt="motionLAB Logo" className="h-24 w-auto object-contain" />
                    <CardTitle>Neues Passwort setzen</CardTitle>
                </CardHeader>
                <CardContent>
                    {!hasLink ? (
                        <div className="space-y-4 text-center">
                            <Alert variant="destructive">
                                <AlertDescription>
                                    Dieser Link ist unvollständig. Bitte öffne den Link aus der E-Mail erneut oder fordere
                                    einen neuen an.
                                </AlertDescription>
                            </Alert>
                            <Link className="text-sm text-primary underline" to="/passwort-vergessen">
                                Neuen Link anfordern
                            </Link>
                        </div>
                    ) : (
                    <form
                        className="space-y-4"
                        onSubmit={(event) => {
                            event.preventDefault()
                            resetMutation.mutate()
                        }}
                    >
                        <div className="space-y-2">
                            <Label htmlFor="password">Neues Passwort</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                        </div>

                        {resetMutation.isError && (
                            <Alert variant="destructive">
                                <AlertDescription>{getErrorMessage(resetMutation.error)}</AlertDescription>
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={resetMutation.isPending}
                        >
                            {resetMutation.isPending ? 'Wird gespeichert...' : 'Passwort speichern'}
                        </Button>
                    </form>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
